/**
 * The list of screens, as the navigation sees it.
 *
 * register.tsx fills it; the header switcher and the phone tab bar both read
 * it. The phone bar used to keep its own hardcoded copy, which is how Supply
 * shipped with no way to reach it on a phone. A screen added here appears in
 * every navigation surface without either of them being edited.
 */

import type { LucideIcon } from 'lucide-react';
import { useMemo, useSyncExternalStore } from 'react';

import { useCanAdminister, useHasRole } from '@/stores/authStore';
import type { ScreenId } from '@/stores/workspaceStore';

export interface ScreenEntry {
  id: ScreenId;
  label: string;
  /** Shorter label for the phone tab bar, where 72px is the whole cell. */
  short?: string;
  icon: LucideIcon;
  /** Who may open it. Undefined means everyone signed in. */
  requires?: 'operator' | 'admin';
}

let entries: ScreenEntry[] = [];
const listeners = new Set<() => void>();

export function registerScreens(list: ScreenEntry[]): void {
  entries = [...list];
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useScreens(): ScreenEntry[] {
  return useSyncExternalStore(
    subscribe,
    () => entries,
    () => entries,
  );
}

export interface ScreenAccess {
  canPlan: boolean;
  isAdmin: boolean;
}

export function canOpenScreen(screen: ScreenEntry, access: ScreenAccess): boolean {
  if (screen.requires === 'admin') return access.isAdmin;
  if (screen.requires === 'operator') return access.canPlan;
  return true;
}

export function useScreenAccess(): ScreenAccess {
  return { canPlan: useHasRole('operator'), isAdmin: useCanAdminister() };
}

/** The screens this user may open, in registry order. */
export function useAvailableScreens(): ScreenEntry[] {
  const all = useScreens();
  const { canPlan, isAdmin } = useScreenAccess();
  return useMemo(() => all.filter((screen) => canOpenScreen(screen, { canPlan, isAdmin })), [all, canPlan, isAdmin]);
}

/**
 * The number key that jumps to each screen: its place in the registry, 1 to 9.
 * Keyed by registry position rather than by what this user may open, so 2 is
 * Planning for everybody and a key never changes meaning with a role. The
 * palette and these keys used to keep their own lists, which is how Data and
 * Training shipped with no key at all.
 */
export function screenHotkeys(screens: ScreenEntry[]): Array<{ key: string; screen: ScreenEntry }> {
  return screens.slice(0, 9).map((screen, index) => ({ key: String(index + 1), screen }));
}

/** Read outside React, by the key handler. */
export function registeredScreens(): ScreenEntry[] {
  return entries;
}

/**
 * Split the screens across a tab bar of `slots` cells. When they all fit they
 * all show; otherwise the last cell becomes More and holds the rest, so the
 * bar never squeezes six labels into a 360px phone.
 */
export function splitTabs<T>(screens: T[], slots: number): { tabs: T[]; overflow: T[] } {
  if (screens.length <= slots) return { tabs: screens, overflow: [] };
  const keep = Math.max(1, slots - 1);
  return { tabs: screens.slice(0, keep), overflow: screens.slice(keep) };
}
